import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Card,
  Button,
  Row,
  Col,
  Collapse,
  Form,
  InputNumber,
  Input,
  message,
  Checkbox,
  Alert,
} from "antd";
import axios from "axios";

import {
  CaretRightOutlined,
  LockOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
} from "@ant-design/icons";
import DragUpload from "../Form/DragUpload";
import config from "../../config/app"
import CodOffsetPanel from "./CodOffsetPanel";
import { getOffsetSummary, formatCents, toCents } from "../../helpers/codOffset";

// Only a 4xx means the server refused before queuing anything. A network error, timeout or 5xx
// may have queued the payout already, so those must not be offered for retry.
const isDefinitiveRefusal = (err) => {
  const status = err.response?.status
  return status >= 400 && status < 500
}

const PayoutForm = ({ withdrawalId }) => {
  const [checkedPayouts, setCheckedPayouts] = useState([]);
  const [selectedPayouts, setSelectedPayouts] = useState({})
  const [triggeredPayouts, setTriggeredPayouts] = useState([]);
  const [remark, setRemark] = useState(null);
  // COD offset (PD-2333), per merchant id: ticked charge ids (start from the server's suggestion)
  // and the adjustment value typed in that merchant's form
  const [offsetSelections, setOffsetSelections] = useState({});
  const [adjustments, setAdjustments] = useState({});

  const notFoundPayouts = checkedPayouts.find(
    (checkedPayout) => checkedPayout.merchant === null
  )?.data;
  const readyPayouts = checkedPayouts
    .filter((checkedPayout) => checkedPayout.merchant)
    .filter(
      (checkedPayout) =>
        !triggeredPayouts.includes(checkedPayout.merchant?.objectId)
    );
  const readyPayoutsWithReadyOrders = readyPayouts
    .filter((readyPayout) => readyPayout.isReadyAmount > 0)

  const offsetSummaryFor = (report, codAmount) =>
    getOffsetSummary({
      report,
      selectedIds: offsetSelections[report.merchant?.objectId],
      codAmount,
      adjustmentValue: adjustments[report.merchant?.objectId],
    })
  // chargeIds and billingPeriod are only sent for a merchant the server offered an offset for
  // ([] = offset nothing); billingPeriod is the preview's value, sent back unchanged
  const chargeIdsFor = (report) =>
    report.codOffset
      ? {
          chargeIds: offsetSelections[report.merchant.objectId] || [],
          billingPeriod: report.codOffset.billingPeriod,
        }
      : {}

  // Payout ALL sends no adjustment, so the offset is the only thing that changes the amount
  const allSummaries = readyPayoutsWithReadyOrders.map((readyPayout) => ({
    readyPayout,
    summary: getOffsetSummary({
      report: readyPayout,
      selectedIds: offsetSelections[readyPayout.merchant.objectId],
      codAmount: readyPayout.isReadyAmount,
    }),
  }))
  const payableSummaries = allSummaries.filter(({ summary }) => !summary.blockReason)
  const blockedSummaries = allSummaries.filter(({ summary }) => summary.blockReason)

  const merchantsWithUnsentAdjustment = payableSummaries.filter(
    ({ readyPayout }) => toCents(adjustments[readyPayout.merchant.objectId]) !== 0
  )

  const payoutAll = () => {
    const confirmationMessage = (
      <div>
        <p>
          <strong>
            After clicking confirm, will create {payableSummaries.length} Payout
            Transactions to following Merchants:
          </strong>
        </p>
        {merchantsWithUnsentAdjustment.length > 0 && (
          <Alert
            type="warning"
            showIcon
            className="mb-2"
            message="Payout ALL does not send adjustments. These merchants have a typed adjustment that will be ignored; pay them one by one to include it:"
            description={merchantsWithUnsentAdjustment.map(({ readyPayout }) => readyPayout.merchant.user.email).join(", ")}
          />
        )}
        <ol>
          {payableSummaries.map(({ readyPayout, summary }) => (
            <li key={readyPayout.merchant.objectId}>
              {readyPayout.merchant.user.email}: RM {formatCents(summary.remainingCents)}
              {summary.offsetCents !== 0 && (
                <span>
                  {" "}
                  (COD RM {readyPayout.isReadyAmount.toFixed(2)}, COD offset -RM {formatCents(summary.offsetCents)})
                </span>
              )}
            </li>
          ))}
        </ol>
      </div>
    );

    Modal.confirm({
      content: confirmationMessage,
      title: `Are you sure to proceed with Payout ALL?`,
      okText: `Payout RM ${formatCents(
        payableSummaries.reduce((total, { summary }) => total + summary.remainingCents, 0)
      )}`,
      onOk() {
        const paidMerchantIds = payableSummaries.map(({ readyPayout }) => readyPayout.merchant.objectId)
        // Append, don't replace: merchants already paid one at a time must stay hidden
        setTriggeredPayouts((prev) => [...new Set([...prev, ...paidMerchantIds])]);
        // A merchant brought back after a 4xx remounts with an empty form, so drop its stale adjustment
        setAdjustments((prev) => {
          const next = { ...prev };
          paidMerchantIds.forEach((id) => delete next[id]);
          return next;
        });

        // One merchant's rejection (e.g. a 422 from the offset checks) must not hide the others
        const payoutPromises = payableSummaries.map(async ({ readyPayout }) => {
          try {
            await axios.post("/api/payout/pay", {
              orderIds: readyPayout.data
                .filter((order) => order.isReady)
                .map((order) => order.id),
              merchantId: readyPayout.merchant.objectId,
              amount: readyPayout.isReadyAmount,
              remark,
              withdrawalId,
              ...chargeIdsFor(readyPayout),
            });
            return null;
          } catch (err) {
            return {
              readyPayout,
              error: err.response?.data?.message || err.message,
              canRetry: isDefinitiveRefusal(err),
            };
          }
        });

        Promise.all(payoutPromises).then((results) => {
          const failures = results.filter(Boolean);
          const retryableIds = failures
            .filter(({ canRetry }) => canRetry)
            .map(({ readyPayout }) => readyPayout.merchant.objectId)

          if (failures.length > 0) {
            // Bring back only the merchants the server refused, so they can be fixed and paid again
            setTriggeredPayouts((prev) => prev.filter((id) => !retryableIds.includes(id)));
            Modal.error({
              title: `${failures.length} Payout(s) could not be requested`,
              content: (
                <div>
                  <ul>
                    {failures.map(({ readyPayout, error, canRetry }) => (
                      <li key={readyPayout.merchant.objectId}>
                        {readyPayout.merchant.user?.email}: {error}
                        {!canRetry && " (no clear answer from the server)"}
                      </li>
                    ))}
                  </ul>
                  {failures.some(({ canRetry }) => !canRetry) && (
                    <p>
                      Merchants marked "no clear answer" may already have a payout queued, so they stay hidden.
                      Check the Payout Status before trying again.
                    </p>
                  )}
                </div>
              ),
            });
          }

          if (failures.length < results.length) {
            message.info(
              `Bulk Payouts are being processed. Please check the Payout Status after some time.`
            );
          }

          if (failures.length === 0 && blockedSummaries.length === 0) {
            setSelectedPayouts({});
            setOffsetSelections({});
            setCheckedPayouts([]);
            setRemark(null);
          } else {
            // Keep merchants that were blocked or refused on screen; drop the ones just paid or awaiting a result
            const succeededIds = paidMerchantIds.filter((id) => !retryableIds.includes(id))
            // Their keys must go too, or the totals loop reaches the trailing { merchant: null } entry and throws.
            // Clear them before checkedPayouts: React 16 does not batch updates inside a promise callback.
            const withoutSucceeded = (prev) => {
              const next = { ...prev };
              succeededIds.forEach((id) => delete next[id]);
              return next;
            };
            setSelectedPayouts(withoutSucceeded);
            setOffsetSelections(withoutSucceeded);
            setCheckedPayouts((prev) => prev.filter((p) => !succeededIds.includes(p.merchant?.objectId)));
          }
        });
      },
    });
  };

  const payoutOrders = (readyPayout, orderIds, codAmount, adjustment) => {
    const merchant = readyPayout.merchant
    const offset = offsetSummaryFor(readyPayout, codAmount)
    if (adjustment.adjustmentValue && !adjustment.adjustmentRemark) {
      Modal.error({
        title: "Unable to proceed",
        content: "Adjustment Remark is required.",
      });
      return;
    }

    if (
      !merchant.bankName ||
      !merchant.bankAccount ||
      !merchant.bankHolderName
    ) {
      Modal.error({
        title: "Unable to proceed",
        content: "Merchant has insufficient bank details.",
      });
      return;
    }

    let earlyChargeValue
    if (withdrawalId) {
      earlyChargeValue = -(codAmount * (config.earlyWithdrawalRate / 100))
    }

    if (offset.blockReason) {
      Modal.error({ title: "Unable to proceed", content: offset.blockReason });
      return;
    }

    const grandTotal = (
      codAmount + (adjustment.adjustmentValue || 0) + + (earlyChargeValue || 0) - offset.offsetCents / 100
    ).toFixed(2);

    const confirmationMessage = (
      <div>
        Payout to: <strong>{merchant.bankHolderName}</strong>
        <br />
        Bank: <strong>{merchant.bankName}</strong>
        <br />
        Account No.: <strong>{merchant.bankAccount}</strong>
        <br />
        Remark: <strong>{remark}</strong>
        <br />
        <br />
        <p>
          <strong>
            COD Total: RM {codAmount.toFixed(2)}
            <br />
            {readyPayout.codOffset && (
              <span>
                COD Offset: <strong>-RM {formatCents(offset.offsetCents)}</strong>
                <br />
              </span>
            )}
            <span>
              Adjustment:{" "}
              <strong>RM {adjustment.adjustmentValue?.toFixed(2) || 0}</strong>
              <br />
              Adjustment Remark:{" "}
              <strong>{adjustment.adjustmentRemark || ""}</strong>
              <br />
            </span>
            {!!earlyChargeValue && withdrawalId && (
              <span>
                Early Withdrawal Charge:{" "}
                <strong>{earlyChargeValue ? earlyChargeValue.toFixed(2) : "-"}</strong>
                <br />
              </span>
            )}
            TOTAL: RM {grandTotal}
          </strong>
        </p>
      </div>
    );

    Modal.confirm({
      content: confirmationMessage,
      title: `Are you sure to proceed with Payout?`,
      okText: `Payout RM ${grandTotal}`,
      onOk() {
        setTriggeredPayouts((prev) => [...prev, merchant.objectId]);

        axios
          .post("/api/payout/pay", {
            orderIds,
            merchantId: merchant.objectId,
            amount: codAmount,
            remark,
            withdrawalId,
            ...chargeIdsFor(readyPayout),
            ...adjustment,
          })
          .then((res) => {
            message.info(
              `Payout are being processed. Please check the Payout Status after some time.`
            );
          })
          .catch((err) => {
            // Bring the merchant back to fix and retry only on a definitive refusal
            if (isDefinitiveRefusal(err)) {
              setTriggeredPayouts((prev) => prev.filter((id) => id !== merchant.objectId));
              // The form remounts empty, so drop the adjustment it no longer shows
              setAdjustments((prev) => {
                const next = { ...prev };
                delete next[merchant.objectId];
                return next;
              });
            }
            Modal.error({
              title: `Error when requesting Payout`,
              content: err.response?.data?.message || err.message,
            });
          });
      },
    });
  };

  const setDefaultAll = useCallback(async (checkedPayouts) => {
    const selectedPayouts = {}
    const nextOffsetSelections = {}

    checkedPayouts
      .filter(merchantPayouts => !!merchantPayouts.merchant)
      .forEach(merchantPayouts => {
        selectedPayouts[merchantPayouts.merchant.objectId] = merchantPayouts
          .data
          .filter(payout => payout.isReady === true)
          .map(payout => payout.id)

        if (merchantPayouts.codOffset) {
          nextOffsetSelections[merchantPayouts.merchant.objectId] = merchantPayouts.codOffset.suggestedChargeIds
        }
      })

    setSelectedPayouts(selectedPayouts)
    setOffsetSelections(nextOffsetSelections)
    setAdjustments({})
  }, [])

  // The only way back to the Upload panel after a partial PAYOUT ALL leaves merchants on screen
  const startOver = () => {
    Modal.confirm({
      title: "Start over?",
      content: "This clears the merchants on screen so you can upload a new file. Payouts already requested are not affected.",
      okText: "Start over",
      onOk() {
        setCheckedPayouts([]);
        setSelectedPayouts({});
        setOffsetSelections({});
        setAdjustments({});
        setTriggeredPayouts([]);
        setRemark(null);
      },
    });
  };

  const handleUpload = (res) => {
    setTriggeredPayouts([]);
    setCheckedPayouts(res.data.data);
    setDefaultAll(res.data.data)
  };

  useEffect(() => {
    if (withdrawalId) {
      axios.post(`${process.env.REACT_APP_SERVER_URL}/api/payout/${withdrawalId}/process-withdrawal`)
        .then(res => {
          const checkedPayouts = res.data.data

          setCheckedPayouts(checkedPayouts);
          setDefaultAll(checkedPayouts)
        })
    }
  }, [withdrawalId, setDefaultAll]);

  const totals = {}
  Object.keys(selectedPayouts).forEach((merchantId) => {
    const merchantPayouts = checkedPayouts.find(
      (checked) => checked.merchant?.objectId === merchantId
    );

    if (!merchantPayouts) {
      totals[merchantId] = 0;
      return;
    }

    totals[merchantId] = merchantPayouts.data
      .filter((payout) =>
        selectedPayouts[merchantId]?.includes(payout.id)
      )
      .reduce((total, payout) => total + payout.cod, 0);
  });

  const totalUnreadyAmount = readyPayouts.reduce(
    (sum, p) => sum + (p.unReadyAmount || 0),
    0
  )

  // Totals shown on the Payout ALL panel cover only the merchants it will actually pay, net of the
  // COD offset, which gets its own line. Blocked merchants are listed in the alert instead.
  const totalOffsetCents = payableSummaries.reduce((sum, { summary }) => sum + summary.offsetCents, 0)
  const totalReadyAmount = payableSummaries.reduce((sum, { summary }) => sum + summary.remainingCents, 0) / 100
  const totalAllAmount = totalReadyAmount + totalUnreadyAmount

  return (
    <div>
      {checkedPayouts.length > 0 && !withdrawalId && (
        <div className="mt-2" style={{ textAlign: "right" }}>
          <Button onClick={startOver}>Start over</Button>
        </div>
      )}
      {checkedPayouts.length > 0 && (
        <Collapse
          className="mt-2"
          size="small"
          defaultActiveKey={[
            ...(notFoundPayouts ? ["notFound"] : []),
            ...readyPayouts
              .map((readyPayout) => readyPayout.merchant?.objectId)
              .slice(0, 5),
            "ALL",
          ]}
          expandIcon={({ isActive }) => (
            <CaretRightOutlined rotate={isActive ? 90 : 0} />
          )}
        >
          {notFoundPayouts.length > 0 && (
            <Collapse.Panel header="Payout: NOT FOUND" key="notFound">
              <h4 style={{ color: "red" }}>
                <strong>
                  <CloseCircleFilled /> Unable to proceed
                </strong>
              </h4>
              <ol>
                {notFoundPayouts.map((order) => (
                  <li key={order.consign_no}>
                    {order.consign_no}: {order.message}
                  </li>
                ))}
              </ol>
            </Collapse.Panel>
          )}

          {readyPayouts.map((readyPayout, index) => {
            const readyOrders = readyPayout.data.filter(
              (order) => order.isReady
            );

            return (
              <Collapse.Panel
                header={
                  <span>
                    Payout {index + 1}: {readyPayout.merchant?.user?.email || ""}
                    ({readyPayout.merchant?.bankHolderName || "-"}) :{" "}
                    {readyPayout.isBeneficiaryVerified === false && (
                      <span style={{ color: "red", fontWeight: "bold" }}>
                        Merchant Beneficiary not Verified. Kindly change user's Payout Payment Provider to CommercePay
                      </span>
                    )}
                  </span>
                }
                key={readyPayout.merchant?.objectId}
              >
                <h4 style={{ color: "green" }}>
                  <strong>
                    <CheckCircleFilled /> Ready (Total: RM{" "}
                    {totals[readyPayout.merchant?.objectId]?.toFixed(2)})
                  </strong>
                </h4>
                <ol>
                  {readyOrders.map((order) => (
                    <li key={order.consign_no}>
                      {withdrawalId && <Checkbox
                        checked={selectedPayouts[readyPayout.merchant?.objectId]?.includes(order.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedPayouts(prev => {
                              return {
                                ...prev,
                                [readyPayout.merchant?.objectId]: [...new Set([...prev[readyPayout.merchant?.objectId], order.id])]
                              }
                            })
                          } else {
                            setSelectedPayouts(prev => {
                              return {
                                ...prev,
                                [readyPayout.merchant?.objectId]: prev[readyPayout.merchant?.objectId].filter(payout => payout !== order.id)
                              }
                            })
                          }
                        }}>
                      </Checkbox>}{` `}
                      {order.consign_no}: RM {order.cod?.toFixed(2)}{" "}
                      {order.currency ? `(from ${order.currency})` : ""}
                    </li>
                  ))}
                  {withdrawalId &&
                    <li key="early-charge">Early Withdrawal Charge: -RM {(totals[readyPayout.merchant?.objectId] * (config.earlyWithdrawalRate / 100))?.toFixed(2)}</li>
                  }
                </ol>

                {readyPayout.codOffsetError && (
                  <Alert
                    className="mb-2"
                    type="error"
                    showIcon
                    message={`COD offset could not be loaded: ${readyPayout.codOffsetError}`}
                    description="Payout is blocked for this merchant until the offset can be loaded."
                  />
                )}

                {readyPayout.codOffset && (
                  <CodOffsetPanel
                    codOffset={readyPayout.codOffset}
                    selectedIds={offsetSelections[readyPayout.merchant?.objectId] || []}
                    onChange={(ids) =>
                      setOffsetSelections((prev) => ({ ...prev, [readyPayout.merchant.objectId]: ids }))
                    }
                    codAmount={totals[readyPayout.merchant?.objectId] || 0}
                    summary={offsetSummaryFor(readyPayout, totals[readyPayout.merchant?.objectId] || 0)}
                  />
                )}

                {readyPayout.isReadyAmount > 0 && (
                  <Row justify="center">
                    <Col span="8">
                      <Card>
                        <Form
                          layout="vertical"
                          name={`payout-form-${readyPayout.merchant?.objectId}`}
                          onValuesChange={(changed) => {
                            if ("adjustmentValue" in changed) {
                              setAdjustments((prev) => ({
                                ...prev,
                                [readyPayout.merchant.objectId]: changed.adjustmentValue,
                              }))
                            }
                          }}
                          onFinish={(formValues) =>
                            payoutOrders(
                              readyPayout,
                              selectedPayouts[readyPayout.merchant?.objectId],
                              totals[readyPayout.merchant?.objectId],
                              formValues
                            )
                          }
                        >
                          {withdrawalId &&
                            <Form.Item
                              label="Remark (Required)"
                              name="remark"
                            >
                              <Input
                                value={remark}
                                onChange={(e) => setRemark(e.target.value)}
                              />
                            </Form.Item>
                          }
                          <Form.Item
                            label="Adjustment (RM)"
                            name="adjustmentValue"
                          >
                            <InputNumber
                              placeholder="Optional"
                              step={0.01}
                              max={0}
                              min={-totals[readyPayout.merchant?.objectId]}
                            />
                          </Form.Item>
                          <Form.Item
                            label="Adjustment Remark"
                            name="adjustmentRemark"
                          >
                            <Input.TextArea
                              rows={3}
                              placeholder="Required if there is adjustment"
                            />
                          </Form.Item>
                          <Form.Item>
                            <Button
                              size="large"
                              type="primary"
                              disabled={
                                !remark ||
                                !!offsetSummaryFor(readyPayout, totals[readyPayout.merchant?.objectId] || 0).blockReason
                              }
                              htmlType="submit"
                            >
                              <LockOutlined /> PAYOUT
                            </Button>
                          </Form.Item>
                        </Form>
                      </Card>
                    </Col>
                  </Row>
                )}

                <h4 style={{ color: "red" }}>
                  <strong>
                    <CloseCircleFilled /> Unable to proceed
                  </strong>
                </h4>
                <ol>
                  {readyPayout.data
                    .filter((order) => !order.isReady)
                    .map((order) => (
                      <li key={order.consign_no}>
                        {order.consign_no}: {order.message}
                      </li>
                    ))}
                </ol>
              </Collapse.Panel>
            );
          })}

          {!withdrawalId && readyPayoutsWithReadyOrders.length > 0 && (
            <Collapse.Panel header={<span style={{ color: "blue", fontWeight: "bold" }}>Payout ALL (Total of ALL Payouts: RM {totalAllAmount.toFixed(2)} )</span>} key="ALL">
              {/* READY */}
              <h4 style={{ color: "green" }}>
                <strong>
                  <CheckCircleFilled /> Ready (Total of ALL Ready Amount: RM{" "}
                  {totalReadyAmount.toFixed(2)}
                  )
                </strong>
              </h4>
              {totalOffsetCents !== 0 && (
                <p>
                  COD offset (already deducted from the amounts below): -RM {formatCents(totalOffsetCents)}
                </p>
              )}
              {blockedSummaries.length > 0 && (
                <Alert
                  className="mb-2"
                  type="error"
                  showIcon
                  message="These merchants are blocked and will NOT be paid by Payout ALL:"
                  description={
                    <ul>
                      {blockedSummaries.map(({ readyPayout, summary }) => (
                        <li key={readyPayout.merchant.objectId}>
                          {readyPayout.merchant.user?.email}: {summary.blockReason}
                        </li>
                      ))}
                    </ul>
                  }
                />
              )}

              <ol>
                {payableSummaries.map(({ readyPayout: payout, summary }, index) => {
                  if (payout.isBeneficiaryVerified !== false) {
                    return (
                      <li key={payout.merchant?.objectId}>
                        Payout {index + 1}: {payout.merchant?.user?.email}: RM{" "}
                        {formatCents(summary.remainingCents)}
                        {summary.offsetCents !== 0 && (
                          <span>
                            {" "}
                            (COD RM {(payout.isReadyAmount || 0).toFixed(2)}, COD offset -RM {formatCents(summary.offsetCents)})
                          </span>
                        )}
                      </li>
                    )
                  }
                })}
              </ol>

              {/* UNREADY — show only if total > 0 */}
              {readyPayouts.some(p => (p.unReadyAmount || 0) > 0) && (
                <>
                  <h4 style={{ color: "red", marginTop: 16 }}>
                    <strong>
                      <CloseCircleFilled /> Unready (Total of ALL Unready Amount: RM{" "}
                      {readyPayouts
                        .reduce((total, p) => total + (p.unReadyAmount || 0), 0)
                        .toFixed(2)}
                      ).
                    </strong>

                    <span style={{ color: "red", fontWeight: "bold" }}>
                      {" "} Kindly change user's Payout Payment Provider to CommercePay
                    </span>
                  </h4>

                  <ol>
                    {readyPayouts
                      .filter(p => (p.unReadyAmount || 0) > 0)
                      .map((payout, index) => (
                        <li key={`${payout.merchant?.objectId}-unready`}>
                          Payout {index + 1}: {payout.merchant?.user?.email}: RM{" "}
                          {(payout.unReadyAmount || 0).toFixed(2)}
                        </li>
                      ))}
                  </ol>
                </>
              )}


              <div className="text-center mt-4">
                <Button size="large" type="primary" disabled={payableSummaries.length === 0} onClick={payoutAll}>
                  <LockOutlined /> PAYOUT ALL <LockOutlined />
                </Button>
              </div>
            </Collapse.Panel>
          )}

        </Collapse>
      )}

      {checkedPayouts.length === 0 && !withdrawalId && (
        <Collapse
          expandIcon={({ isActive }) => (
            <CaretRightOutlined rotate={isActive ? 90 : 0} />
          )}
          className="mt-2"
          size="small"
          defaultActiveKey={["upload-form"]}
        >
          <Collapse.Panel header="Upload" key="upload-form">
            <div className="mb-2">
              <Input
                addonBefore={<span>Remark <span style={{ color: "red" }}>*</span></span>}
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
              />
            </div>

            <DragUpload
              disabled={!remark}
              action={`${process.env.REACT_APP_SERVER_URL}/api/payout/process-bulk`}
              accept=".xls, .xlsx"
              method="post"
              onUploadSuccess={(res) => handleUpload(res)}
              additionalDesc={`Excel file with "Payout Tracking Number" header`}
            />
          </Collapse.Panel>
        </Collapse>
      )}
    </div>
  );
};

export default PayoutForm;
